import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package } from "@/types";
import { X, Maximize2, Archive, Weight } from "lucide-react";
import { useState, useEffect } from "react";
import { usePackageStore } from "@/stores/packageStore";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { toast } from "sonner";
import StatusTimeline from "@/components/shared/StatusTimeline";
import StatusBadge from "@/components/StatusBadge";
import { usePackageHistory } from "@/hooks/usePackageHistory";

interface PackageInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageData: Package | null;
  onSave?: (id: number, changes: any) => Promise<void>;
}

const PackageInfoModal = ({
  open,
  onOpenChange,
  packageData
}: PackageInfoModalProps) => {
  const { data: history, isLoading } = usePackageHistory(packageData?.id);
  if (!packageData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Package Information</DialogTitle>
          <DialogClose className="absolute right-4 top-4">
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="space-y-6 p-2">
          {/* Header section: ID, badge, signature */}
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Package ID</div>
              <div className="font-semibold text-2xl leading-tight">{String(packageData.sequential_number).padStart(4, '0')}</div>
              <span className="inline-block mt-1"><StatusBadge status={packageData.status} /></span>
            </div>
            <div>
              {packageData.signature ? (
                <img src={packageData.signature} alt="Signature" className="w-20 h-20 object-contain rounded shadow border bg-white" />
              ) : (
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 text-gray-400 rounded shadow border text-xs">No signature provided</div>
              )}
            </div>
          </div>
          {/* Details grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Tracking Number</div>
              <div className="text-sm font-medium break-words">{packageData.tracking}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Guest Name</div>
              <div className="text-sm">{packageData.guest_name}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Unit Number</div>
              <div className="text-sm">{packageData.room_number || 'N/A'}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Entry Date</div>
              <div className="text-sm">{packageData.entry_date ? new Date(packageData.entry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Status</div>
              <div className="text-sm">{packageData.status}</div>
            </div>
          </div>
          {/* Status Timeline */}
          {isLoading ? (
            <div>Loading timeline...</div>
          ) : (
            (() => {
              // Build timeline: always include creation line
              const timeline = (history || []).map(h => ({
                status: h.status,
                date: h.timestamp,
                updatedBy: h.updated_by
              }));
              // Check if a creation event exists
              const hasCreation = timeline.some(e => e.status === 'Created');
              if (!hasCreation && packageData.entry_date && packageData.last_updated_by) {
                timeline.unshift({
                  status: 'Created',
                  date: packageData.entry_date,
                  updatedBy: packageData.last_updated_by
                });
              }
              return <StatusTimeline history={timeline} />;
            })()
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackageInfoModal;
